import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, BuildingIcon, GlassWaterIcon, ChefHatIcon, BriefcaseIcon } from 'lucide-react';
export function FacilityBookingPage({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="min-h-screen w-full bg-[#050505] pt-24 pb-20 px-4 sm:px-6" data-id="element-1019">
      <div className="max-w-5xl mx-auto" data-id="element-1020">
        {/* Back */}
        <motion.button initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.4
      }} onClick={onBack} className="flex items-center gap-2 font-montserrat text-[12px] tracking-widest text-white/40 hover:text-[#C9A84C] transition-colors duration-300 mb-10" data-id="element-1021">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-1022">
            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1023" />
          </svg>
          BACK TO HOME
        </motion.button>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-1024">
          <span className="font-inter text-[13px] tracking-[0.4em] text-[#C9A84C]/60 uppercase" data-id="element-1025">
            Host Your Event
          </span>
          <h1 className="font-montserrat font-black text-4xl sm:text-5xl tracking-widest text-white mt-2" data-id="element-1026">
            FACILITY BOOKING
          </h1>
          <div className="w-14 h-px bg-[#C9A84C]/50 mx-auto mt-4 mb-5" data-id="element-1027" />
          <p className="font-inter font-light text-[16px] text-white/55 max-w-2xl mx-auto leading-relaxed" data-id="element-1028">
            MK Lounge on U is a modern lounge designed to host unforgettable
            gatherings. From intimate celebrations to large corporate events,
            our versatile space is equipped to handle it all.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-1029">
          {[{
          icon: <BuildingIcon size={28} data-id="element-1030" />,
          title: '2 MODERN FLOORS',
          desc: 'Our expansive venue features two distinct levels, allowing for versatile event setups, separate VIP areas, or multi-stage entertainment.'
        }, {
          icon: <GlassWaterIcon size={28} data-id="element-1031" />,
          title: '2 FULLY STOCKED BARS',
          desc: 'With a dedicated, fully stocked bar on each floor, your guests will never have to wait long for our signature craft cocktails and premium spirits.'
        }, {
          icon: <ChefHatIcon size={28} data-id="element-1032" />,
          title: 'FULL KITCHEN',
          desc: "Our in-house full kitchen is ready to cater your event, offering everything from light hors d'oeuvres to complete custom dining menus."
        }, {
          icon: <BriefcaseIcon size={28} data-id="element-1033" />,
          title: 'CORPORATE & PRIVATE EVENTS',
          desc: "We are fully equipped to handle any event type. Whether it's a corporate holiday party, a product launch, or a private birthday bash, we have you covered."
        }].map((feature, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-[#0a0a0a] border border-[#2A2622] rounded-3xl p-8 hover:border-[#C9A84C]/40 transition-colors duration-300" data-id="element-1034">
              <div className="w-14 h-14 rounded-2xl bg-[#C9A84C]/10 flex items-center justify-center text-[#C9A84C] mb-6 border border-[#C9A84C]/20" data-id="element-1035">
                {feature.icon}
              </div>
              <h3 className="font-montserrat font-black text-xl tracking-widest text-white mb-3" data-id="element-1036">
                {feature.title}
              </h3>
              <p className="font-inter text-white/50 leading-relaxed" data-id="element-1037">
                {feature.desc}
              </p>
            </motion.div>)}
        </div>

        {/* Contact CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="relative rounded-3xl overflow-hidden p-10 sm:p-16 text-center" style={{
        background: 'linear-gradient(160deg, #0a0500 0%, #1a0800 50%, #2a1200 100%)',
        border: '1px solid rgba(201,168,76,0.3)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
      }} data-id="element-1038">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay" data-id="element-1039" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent" data-id="element-1040" />

          <div className="relative z-10" data-id="element-1041">
            <div className="w-20 h-20 mx-auto bg-[#C9A84C]/10 rounded-full flex items-center justify-center mb-6 border border-[#C9A84C]/30" data-id="element-1042">
              <PhoneIcon size={32} className="text-[#C9A84C]" data-id="element-1043" />
            </div>
            <h2 className="font-montserrat font-black text-3xl sm:text-4xl tracking-widest text-white mb-4" data-id="element-1044">
              READY TO BOOK?
            </h2>
            <p className="font-inter text-lg text-white/70 max-w-xl mx-auto mb-8" data-id="element-1045">
              If you need to book our lounge for an event, call us directly to
              discuss your requirements, dates, and custom packages.
            </p>
            <a href="tel:2025181161" className="inline-flex items-center gap-3 font-montserrat font-black text-lg tracking-widest bg-[#C9A84C] text-[#050505] px-10 py-5 rounded-xl hover:bg-[#E8C97A] transition-all duration-300 hover:scale-105" style={{
            boxShadow: '0 8px 30px rgba(201,168,76,0.3)'
          }} data-id="element-1046">
              <PhoneIcon size={20} data-id="element-1047" />
              (202) 518-1161
            </a>
          </div>
        </motion.div>
      </div>
    </div>;
}